import React, { useState } from 'react';
import { User, Building2, Phone, MapPin, FileText, Lock, Save, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function ClientProfile() {
    const { user, updateUser } = useAuth();
    const [saving, setSaving] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    
    const [profile, setProfile] = useState({
        company_name: user?.company_name || '',
        contact_name: user?.contact_name || '',
        phone: user?.phone || '',
        address: user?.address || '',
        siret: user?.siret || '',
    });
    
    const [passwords, setPasswords] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await authAPI.updateProfile(profile);
            updateUser(res.data);
            toast.success('Profil mis à jour avec succes');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwords.new_password !== passwords.confirm_password) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        if (passwords.new_password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caracteres');
            return;
        }
        setChangingPassword(true);
        try {
            await authAPI.changePassword({
                current_password: passwords.current_password,
                new_password: passwords.new_password,
            });
            toast.success('Mot de passe modifie avec succes');
            setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="client-profile">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                    <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-jungle transition-colors mb-4">
                        <ArrowLeft className="w-4 h-4" />
                        Retour au tableau de bord
                    </Link>
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle" data-testid="profile-title">
                        Mon Profil
                    </h1>
                    <p className="text-muted-foreground mt-1">{user?.email}</p>
                    {user?.client_code && (
                        <p className="text-sm font-medium text-jungle mt-1" data-testid="profile-client-code">
                            Code client : {user.client_code}
                        </p>
                    )}
                </div>

                {/* Profile Form */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-jungle">
                            <User className="w-5 h-5" />
                            Informations personnelles
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleProfileSave} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="company_name" className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-muted-foreground" />
                                        Nom de l'entreprise
                                    </Label>
                                    <Input
                                        id="company_name"
                                        data-testid="profile-company-name"
                                        value={profile.company_name}
                                        onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact_name" className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-muted-foreground" />
                                        Nom du responsable
                                    </Label>
                                    <Input
                                        id="contact_name"
                                        data-testid="profile-contact-name"
                                        value={profile.contact_name}
                                        onChange={(e) => setProfile({...profile, contact_name: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        Telephone
                                    </Label>
                                    <Input
                                        id="phone"
                                        data-testid="profile-phone"
                                        value={profile.phone}
                                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="siret" className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-muted-foreground" />
                                        SIRET
                                    </Label>
                                    <Input
                                        id="siret"
                                        data-testid="profile-siret"
                                        value={profile.siret}
                                        onChange={(e) => setProfile({...profile, siret: e.target.value})}
                                        placeholder="Optionnel"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="address" className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    Adresse de livraison
                                </Label>
                                <Input
                                    id="address"
                                    data-testid="profile-address"
                                    value={profile.address}
                                    onChange={(e) => setProfile({...profile, address: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="profile-save-btn"
                                    className="bg-leaf hover:bg-leaf/90 text-white"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>

                {/* Password Change */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-jungle">
                            <Lock className="w-5 h-5" />
                            Changer le mot de passe
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handlePasswordChange} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="current_password">Mot de passe actuel</Label>
                                <div className="relative">
                                    <Input
                                        id="current_password"
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        data-testid="profile-current-password"
                                        value={passwords.current_password}
                                        onChange={(e) => setPasswords({...passwords, current_password: e.target.value})}
                                        required
                                    />
                                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowCurrentPassword(!showCurrentPassword)} data-testid="toggle-current-password">
                                        {showCurrentPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="new_password">Nouveau mot de passe</Label>
                                    <div className="relative">
                                        <Input
                                            id="new_password"
                                            type={showNewPassword ? 'text' : 'password'}
                                            data-testid="profile-new-password"
                                            value={passwords.new_password}
                                            onChange={(e) => setPasswords({...passwords, new_password: e.target.value})}
                                            required
                                        />
                                        <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowNewPassword(!showNewPassword)} data-testid="toggle-new-password">
                                            {showNewPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                        </Button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
                                    <div className="relative">
                                        <Input
                                            id="confirm_password"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            data-testid="profile-confirm-password"
                                            value={passwords.confirm_password}
                                            onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})}
                                            required
                                        />
                                        <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-testid="toggle-confirm-password">
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button
                                    type="submit"
                                    disabled={changingPassword}
                                    data-testid="profile-change-password-btn"
                                    variant="outline"
                                    className="border-jungle text-jungle hover:bg-jungle/10"
                                >
                                    <Lock className="w-4 h-4 mr-2" />
                                    {changingPassword ? 'Modification...' : 'Changer le mot de passe'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
